import React, { PureComponent, Fragment } from 'react';
import PropTypes from 'prop-types';
import { createTheme, ThemeProvider, StyledEngineProvider } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import { ThroughProvider } from 'react-through';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { Redirect, Route, Switch } from 'react-router-dom';
import enLocale from 'date-fns/locale/en-US';
import deLocale from 'date-fns/locale/de';
import frLocale from 'date-fns/locale/fr';
import jaLocale from 'date-fns/locale/ja';
import { connect } from 'react-redux';
import { IntlProvider } from 'react-intl';
import NoticeTemplateDialog from '../components/dialogs/containers/NoticeTemplateDialog';
import SendNoticeDialog from '../components/dialogs/containers/SendNoticeDialog';
import NotificationContainer from '../util/NotificationContainer';
import asyncComponent from '../util/asyncComponent';
import '../styles/app.scss';
import theme from './themes/cyanTheme';
import AppLocale from '../languageProvider';
import {
  AuthorizedRoute,
  NonAuthorizedRoute,
  /* RestrictedRolesRoute */
} from '../restrictedRoutes';

import SignIn from './SignIn';
import NotFoundPage from './NotFoundPage';
import ResetPassword from './ResetPassword';
import ServerErrorPage from './ServerErrorPage';
import TechnicalWorksError from './TechnicalWorksError';
import { setInitUrl } from 'actions';
import { getCurrentUser, getSchoolByTenant } from 'actions/Profile';
import { TENANT_ID_KEY /* USER_ROLES */ } from 'constants/Values';
import Progress from '../components/Progress';
import { addCookie } from 'util/cookies';
import Config from '../env.config';

const localeMap = {
  en: enLocale,
  de: deLocale,
  fr: frLocale,
  ja: jaLocale,
};

const AsyncAuthorizedApp = asyncComponent(() => import('./index'));
// const AsyncSos = asyncComponent(() => import('./Sos'));

class App extends PureComponent {
  static propTypes = {
    locale: PropTypes.shape({ locale: PropTypes.string }).isRequired,
    signedIn: PropTypes.bool,
    user: PropTypes.object,
    getCurrentUser: PropTypes.func.isRequired,
    getSchoolByTenant: PropTypes.func.isRequired,
    serverError: PropTypes.number,
    isNoticeTemplateModalOpen: PropTypes.bool,
  };

  componentDidMount() {
    // we should save tenant id to cookies.
    // we need this for implementation of restricted images access
    addCookie(TENANT_ID_KEY, Config.TENANT_ID, true);

    if (this.props.signedIn && !this.props.user) {
      this.props.getCurrentUser();
      this.props.getSchoolByTenant();
    }
  }

  getErrorPage = errorCode => (errorCode === 502 ? <TechnicalWorksError /> : <ServerErrorPage />);

  renderAppContainer() {
    const { signedIn } = this.props;

    return (
      <Fragment>
        <Switch>
          <NonAuthorizedRoute path="/signin" component={SignIn} exact />
          <NonAuthorizedRoute path="/reset-password" component={ResetPassword} />
          {/* <RestrictedRolesRoute
            exact
            allowedRoles={[USER_ROLES.STUDENT]}
            path="/sos"
            component={AsyncSos}
          /> */}
          {signedIn && <AuthorizedRoute path="/" component={AsyncAuthorizedApp} />}
          {!signedIn && <Redirect to="/signin" />}
          <Route component={NotFoundPage} />
        </Switch>
        {this.props.isNoticeTemplateModalOpen && <NoticeTemplateDialog />}
        <NotificationContainer />
        <SendNoticeDialog />
      </Fragment>
    );
  }

  render() {
    const { locale, user, serverError } = this.props;
    const currentAppLocale = AppLocale[locale.locale];

    return (
      <StyledEngineProvider injectFirst>
        <ThemeProvider theme={createTheme(theme)}>
        <CssBaseline />
        <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={localeMap[locale.locale]}>
          <ThroughProvider>
            <IntlProvider
              locale={currentAppLocale.locale}
              key={currentAppLocale.locale}
              messages={currentAppLocale.messages}
            >
              <Fragment>
                {serverError ? (
                  this.getErrorPage(serverError)
                ) : (
                  <div className="app-main">
                    {this.props.signedIn && !user ? <Progress /> : this.renderAppContainer()}
                  </div>
                )}
              </Fragment>
            </IntlProvider>
          </ThroughProvider>
        </LocalizationProvider>
        </ThemeProvider>
      </StyledEngineProvider>
    );
  }
}

const mapStateToProps = ({ settings, auth, profile, app, modal }) => ({
  serverError: app.serverError,
  sideNavColor: settings.sideNavColor,
  locale: settings.locale,
  signedIn: auth.signedIn,
  user: profile.user,
  isNoticeTemplateModalOpen: modal.noticeTemplateModal.visible,
});

export default connect(
  mapStateToProps,
  {
    setInitUrl,
    getCurrentUser,
    getSchoolByTenant,
  },
)(App);
